import React, {useEffect, useState} from 'react';
import { LoadingOverlay, Modal, Text, Anchor } from "@mantine/core"

import {
    TokenRequest,
    BaseTokenRequestHandler,
    GRANT_TYPE_AUTHORIZATION_CODE,
    AuthorizationServiceConfiguration,
    RedirectRequestHandler,
    AuthorizationNotifier,
    FetchRequestor, LocalStorageBackend
} from '@openid/appauth';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { generateRandom, deriveChallenge, NoHashQueryStringUtils } from './CryptoUtils';
import { jwtDecode } from 'jwt-decode';

const customCrypto = {
    generateRandom,
    deriveChallenge
};

interface IdToken {
    permissions: {[key: string]: string};
    sessionId: string;
    userName: string;
    identityProvider: string;
    error: string;
}

interface LoginProps {
    updateAuthenticated: (arg: any) => void
}


export const Callback = (props: LoginProps) => {

    const [searchParams, setSearchParams] = useSearchParams();

    const [ssoAuthError, setSsoAuthError] = useState<string|null>(null);
    const [showSsoAuthError, setShowSsoAuthError] = useState(false);
    const [code, setCode] = useState<string|null>(null);
    const [loading, setLoading] = useState(true);

    const navigate = useNavigate();

    function closeLoadingOverlay() {
        setLoading(false);
    }

    function closeSsoAuthErrorDialog() {
        setShowSsoAuthError(false);
    }

    function updateAndShowSsoError(errorResponse?: string) {
        console.log('Updating and showing SSO error:' + errorResponse);
        if ( errorResponse === 'account_disabled' ) {
            setSsoAuthError('The Aerobahn account linked to your provider account has been disabled. Please contact customer service.');
        } else if ( errorResponse === 'access_denied' ) {
            setSsoAuthError('We were unable to find an Aerobahn account linked to your provider account. Please confirm an account exists and contact customer service.');
        } else if ( errorResponse === 'timeout' ) {
            setSsoAuthError('Your session has expired. Please click the button below to return to the sign-in page. If this issue persists, please contact customer service.');
        } else {
            setSsoAuthError('There was an error contacting the Authentication server. Please contact Customer Service.');
        }
        closeLoadingOverlay();
        setShowSsoAuthError(true);
    }

    useEffect(function () {
        const tokenHandler = new BaseTokenRequestHandler(new FetchRequestor());
        const authorizationHandler = new RedirectRequestHandler(new LocalStorageBackend(), NoHashQueryStringUtils, window.location, customCrypto);
        const notifier = new AuthorizationNotifier();
        authorizationHandler.setAuthorizationNotifier(notifier);
        notifier.setAuthorizationListener((request, response, error) => {
            console.log('Authorization request complete ');
            //console.log('Authorization request complete ', request, response, error);
            if (response) {
                let extras = {};
                if (request && request.internal) {
                    extras ={"code_verifier": request.internal.code_verifier};
                }
                
                const tokenRequest = new TokenRequest({
                    client_id: process.env.REACT_APP_OAUTH2_CLIENT_ID as string,
                    redirect_uri: process.env.REACT_APP_OAUTH2_REDIRECT_URI as string,
                    grant_type: GRANT_TYPE_AUTHORIZATION_CODE,
                    code: response.code,
                    extras
                });

                AuthorizationServiceConfiguration.fetchFromIssuer(process.env.REACT_APP_OAUTH2_AUTHORIZATION_SERVER as string, new FetchRequestor())
                    .then((oResponse) => {
                        const configuration = oResponse;
                        return tokenHandler.performTokenRequest(configuration, tokenRequest);
                    })
                    .then((oResponse) => {
                        if (!oResponse.idToken) {
                            updateAndShowSsoError();
                            return;
                        }
                        const decoded = jwtDecode<IdToken>(oResponse.idToken);
                        if ( decoded.error ) {
                            updateAndShowSsoError(decoded.error);
                            return;
                        }

                        if (!decoded.sessionId || !decoded.userName) {
                            updateAndShowSsoError();
                            return;
                        }

                        sessionStorage.setItem('authenticated', 'true');
                        sessionStorage.setItem('sessionId', decoded.sessionId);
                        if ( decoded.identityProvider ) {
                            sessionStorage.setItem('identityProvider', decoded.identityProvider);
                        }
                        if (decoded.permissions["accessControlConfiguration/datablockFields/modifyManualFields/modifyManualTOBT"]) {
                            sessionStorage.setItem('enableEdit', decoded.permissions["accessControlConfiguration/datablockFields/modifyManualFields/modifyManualTOBT"].toLowerCase());
                        }

                        if (decoded.permissions["accessControlConfiguration/userSettings/displayTimesInAirportLocalTimeZone"] != null) {
                            sessionStorage.setItem('isLocalAirportTime', decoded.permissions["accessControlConfiguration/userSettings/displayTimesInAirportLocalTimeZone"].toLowerCase());
                        }
                        if (decoded.permissions["aerobahn.airport.timeZoneLabel"]) {
                            sessionStorage.setItem('timezoneLabel', decoded.permissions["aerobahn.airport.timeZoneLabel"]);
                        }
                        if (decoded.permissions["aerobahn.airport.timeZone"]) {
                            sessionStorage.setItem('timezone', decoded.permissions["aerobahn.airport.timeZone"]);
                        }

                        props.updateAuthenticated(true);
                        
                        return navigate("/");
                    })
                    .catch(oError => {
                        updateAndShowSsoError();
                    });
            }
        });
        
        const errorResponse = searchParams.get('error');
        if ( errorResponse ) {
            updateAndShowSsoError(errorResponse);
            return;
        }
        const code = searchParams.get('code');
        if ( !code ) {
            updateAndShowSsoError();
            return;
        }

        setCode(code);
        authorizationHandler.completeAuthorizationRequestIfPossible();

        if (location.search.includes("code")) {
            const timeoutId = setTimeout(() => {
                updateAndShowSsoError('timeout');
            }, 30000);

            return () => {
                clearTimeout(timeoutId);
            }
        }
        
    }, [code]);

    return (
        <div>
        <LoadingOverlay visible={loading}/>
          <Modal opened={showSsoAuthError} onClose={closeSsoAuthErrorDialog} title="Error">
            <p style={{fontSize:'12.5px'}}>
            {ssoAuthError}
            </p>
            <Text style={{padding:'10px'}} align="center" mt="md">
                  <Anchor<"a">
                      href="/tobt"
                      weight={700}>
                    Return to Sign In
                  </Anchor>
            </Text>
          </Modal>
        </div>
    );
}